import React, { useEffect, useState } from 'react';
import './Calendar.css';
import { parseISO, startOfDay, isSameDay, isAfter, isBefore } from 'date-fns';
import GuestSelection from '../GuestSelection/GuestSelection';

const EST_TZ_OFFSET = -5; // EST is UTC-5, but for DST you may want to use a library for full accuracy

const Calendar = ({ calendarData, checkinDate, checkoutDate, setCheckinDate, setCheckoutDate, onDone, onMonthChange, visibleMonth, loading, error, onSelectDates, guests, setGuests }) => {
  const [isGuestSelectionOpen, setIsGuestSelectionOpen] = useState(false);
  const currentDate = visibleMonth || (checkinDate ? parseISO(checkinDate) : new Date());
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const startPadding = firstDay.getDay();

  const days = [];
  for (let i = 0; i < startPadding; i++) {
    days.push(null);
  }
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayData = calendarData?.find(d => d.date === dateStr);
    days.push({ day, date: dateStr, data: dayData });
  }

  // Debug logs for date selection
  console.log('checkinDate:', checkinDate, 'parsed:', checkinDate ? parseISO(checkinDate) : null);
  console.log('checkoutDate:', checkoutDate, 'parsed:', checkoutDate ? parseISO(checkoutDate) : null);

  // Helper to check if a date is in the selected range (EST)
  const isInRange = (dateStr) => {
    if (!checkinDate || !checkoutDate) return false;
    const d = parseISO(dateStr);
    const start = parseISO(checkinDate);
    const end = parseISO(checkoutDate);
    return isAfter(d, start) && isBefore(d, end);
  };

  const isCheckin = (dateStr) => {
    if (!checkinDate) return false;
    return isSameDay(parseISO(dateStr), parseISO(checkinDate));
  };

  const isCheckout = (dateStr) => {
    if (!checkoutDate) return false;
    return isSameDay(parseISO(dateStr), parseISO(checkoutDate));
  };

  // Helper to check if a date is in the past (EST)
  const isPastDate = (dateStr) => {
    const today = startOfDay(new Date());
    return isBefore(parseISO(dateStr), today);
  };

  // Handle day click for range selection (controlled)
  const handleDayClick = (dayInfo) => {
    if (!dayInfo || !dayInfo.data?.price) return;
    const clickedDate = parseISO(dayInfo.date);
    const today = startOfDay(new Date());

    // Don't allow selection of past dates
    if (isBefore(clickedDate, today)) return;

    if (!checkinDate || (checkinDate && checkoutDate)) {
      // Start new selection
      setCheckinDate(dayInfo.date);
      setCheckoutDate(null);
    } else if (checkinDate && !checkoutDate) {
      const currentCheckin = parseISO(checkinDate);
      if (isAfter(clickedDate, currentCheckin)) {
        // Valid checkout date
        setCheckoutDate(dayInfo.date);
      } else if (isSameDay(clickedDate, currentCheckin)) {
        // Same day selection - clear both
        setCheckinDate(null);
        setCheckoutDate(null);
      } else {
        // Earlier date selected - make it the new checkin
        setCheckinDate(dayInfo.date);
        setCheckoutDate(null);
      }
    }
  };

  // Modal header/footer and iOS-style layout
  return (
    <div className="calendar-modal-content">
      <div className="calendar-navbar">
        <button className="navbar-back-btn" onClick={onDone} aria-label="Back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Back</span>
        </button>
        <h2 className="navbar-title">{monthNames[month]} {year}</h2>
        <div className="navbar-month-nav">
            <button className="month-nav-arrow" onClick={onMonthChange?.prev} disabled={loading} aria-label="Previous Month">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <button className="month-nav-arrow" onClick={onMonthChange?.next} disabled={loading} aria-label="Next Month">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 18l6-6-6-6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
        </div>
      </div>
      <div className="calendar-modal-inner">
        <div className="calendar-grid-header">
          {dayNames.map(day => (
            <div key={day} className="calendar-day-header calendar-text-white">{day}</div>
          ))}
        </div>
        <div className="calendar-grid">
          {days.map((dayInfo, index) => {
            if (!dayInfo) return <div key={index} className="calendar-day empty calendar-day-bordered"></div>;
            const { day, date, data } = dayInfo;
            const available = !!data?.price;
            const soldOut = data && !data.price;
            const selected = isCheckin(date) || isCheckout(date);
            const inRange = isInRange(date);
            const disabled = isPastDate(date);
            return (
              <div
                key={index}
                className={`calendar-day calendar-day-bordered${available ? ' available' : ''}${soldOut ? ' sold-out' : ''}${selected ? ' selected' : ''}${inRange ? ' in-range' : ''}${disabled ? ' disabled' : ''}`}
                onClick={() => !disabled && handleDayClick(dayInfo)}
              >
                <div className="calendar-day-number calendar-text-white">{day}</div>
                {available && (
                  <div className="calendar-day-price calendar-text-white">
                    {isCheckout(date) ? 'Departure' : `$${Math.round(data.price)}`}
                  </div>
                )}
                {soldOut && (
                  <div className="calendar-day-soldout-small">Sold out</div>
                )}
              </div>
            );
          })}
        </div>
        {error && <div className="calendar-error calendar-text-white">{error}</div>}
      </div>
      <div className="calendar-modal-footer">
        <div className="calendar-modal-row" onClick={() => setIsGuestSelectionOpen(true)}>
          <span className="calendar-text-white">{guests.adults + guests.children} {guests.adults + guests.children > 1 ? 'Guests' : 'Guest'}</span>
          <span className="calendar-modal-arrow">&rarr;</span>
        </div>
        <button 
          className="calendar-continue-btn" 
          disabled={!(checkinDate && checkoutDate) || loading || error}
          onClick={() => {
            if (checkinDate && checkoutDate) {
              onSelectDates([checkinDate, checkoutDate]);
            }
          }}
        >
          {loading ? <span className="calendar-btn-spinner" /> : 'CONTINUE'}
        </button>
      </div>
      {isGuestSelectionOpen && (
        <GuestSelection 
          guests={guests}
          onGuestsChange={setGuests}
          onClose={() => setIsGuestSelectionOpen(false)}
        />
      )}
    </div>
  );
};

export default Calendar;